import { mediaOrigin } from '@/lib/supabase/media-url'

import OptimizedImage from './optimized-image'
import { storageAssetPath } from './storage-asset-path'
import type { ImageUrlBuilderProps, ImageProps, SupabaseImageProps } from './types'

const toUrl = (builderProps: ImageUrlBuilderProps): string => {
  const { baseUrl, width, quality } = builderProps
  const args = {
    resize: 'contain',
    // crop: 'top,center', not sure how to do this in supabase
    quality: quality || 75,
    width,
  }
  const params = Object.entries(args)
    .filter(([, v]) => !!v)
    .reduce((a, [k, v]) => [...a, `${k}=${v}`], [] as string[])
    .join('&')
  // Note: if you need to test locally and have the asset in storage,
  // replace with this:
  // const url = `http://127.0.0.1:54321/storage/v1/object/public/assets/${baseUrl}?${params}`
  // render/image style doesn't work locally so you don't get transforms
  const url = `${mediaOrigin()}/storage/v1/render/image/public/assets/${baseUrl}?${params}`
  return url
}

// Supabase's local stack serves original objects but not the render/image
// transform endpoint. Preserve a local object URL when one was explicitly
// resolved by a server reader instead of rebuilding it on the production media
// origin. Production and other remote Supabase URLs keep the transformed path.
export const localSupabaseObjectUrl = (source: string): string | null => {
  try {
    const url = new URL(source)
    if (url.hostname !== '127.0.0.1' && url.hostname !== 'localhost') {
      return null
    }
    const path = storageAssetPath(source)
    return path ? `${url.origin}/storage/v1/object/public/assets/${path}` : null
  } catch {
    return null
  }
}

/*
  const { image, imgProps, sizingProps, origin = 'SbAssetImage', tracer, ...rest } = props
  const asset = image as StoryblokAsset
  const { filename, alt, title, focus } = asset
  const [, , , , , dims, , name] = filename!.split('/')
  let [nativeWidth, nativeHeight] = dims.split('x').map((d: string) => Number(d))
  // Clamp nativeWidth to 1620 and adjust nativeHeight to maintain aspect ratio
  if (nativeWidth > 1620) {
    const aspect = nativeWidth / nativeHeight
    nativeWidth = 1620
    nativeHeight = Math.round(nativeWidth / aspect)
  */
const SupabaseImage = (props: ImageProps) => {
  const { image, sizingProps, origin = 'SupabaseImage', tracer, ...rest } = props
  const url = typeof image === 'string' ? image : (image as SupabaseImageProps).image
  // const { image: url } = image as SupabaseImageProps
  let { nativeWidth, nativeHeight } = image as SupabaseImageProps
  if (!nativeWidth || !nativeHeight) {
    const dims = url.match(/.*_([0-9]+x[0-9]+)\./)
    if (dims) {
      const [nw, nh] = dims[1].split('x').map((i: string) => Number(i))
      if (nw && nh && !isNaN(nw) && !isNaN(nh)) {
        nativeWidth = nw
        nativeHeight = nh
      }
    }
  }
  const localObjectUrl = localSupabaseObjectUrl(url)
  const mergedSizingProps = {
    ...sizingProps,
    nativeWidth,
    nativeHeight,
    toUrl: localObjectUrl ? undefined : toUrl,
  }
  // The transform endpoint needs the complete bucket-relative object key.
  const storageAsset = storageAssetPath(url)
  if (nativeWidth && nativeHeight && nativeHeight !== 'auto') {
    const aspectRatio = (1.0 * nativeWidth) / nativeHeight
    if (Number.isNaN(aspectRatio)) {
      console.error(
        '[supabase-image] Bad aspect ratio for supabase image: %s: nw: %o, nh: %o',
        storageAsset,
        nativeWidth,
        nativeHeight
      )
    } else {
      Object.assign(mergedSizingProps, { aspectRatio })
    }
  }
  tracer?.trace('SupabaseImage: %o', { mergedSizingProps, storageAsset })
  return (
    <OptimizedImage
      {...rest}
      tracer={tracer}
      image={localObjectUrl ?? storageAsset}
      sizingProps={mergedSizingProps}
      origin={origin || `supabase-${storageAsset}`}
    />
  )
}

export default SupabaseImage
